import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { login } from '~/store';

import '../style/login.css';

const Login = ({ showHeader, checkAuthorization, setMobileDevice, appLogin, currentYear, trackEvent }) => {
  const dispatch = useDispatch();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [keepLoggedIn, setKeepLoggedIn] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const formValidationState = useMemo(
    () => (username !== '' && password !== '' ? 'valid' : 'invalid'),
    [username, password]
  );

  const handleUsernameChange = useCallback((event) => setUsername(event.target.value), []);
  const handlePasswordChange = useCallback((event) => setPassword(event.target.value), []);
  const handleKeepMeSignedIn = useCallback((event) => setKeepLoggedIn(event.target.checked), []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setLoading(true);

      const response = await dispatch(login({ username, password })).unwrap();
      setLoading(false);

      if (response.status === 400 || response.status === 403 || response.status >= 500) {
        trackEvent(response.status, { source: 'handleSubmit' });
        setErrorMessage(response.message);
        return;
      }

      if (response.status === 401) {
        setErrorMessage('Incorrect username or password. Too many failed attempts will result in temporary lockout.');
        return;
      }

      if (response.authToken) {
        trackEvent('Portal Login');
        await appLogin(response, keepLoggedIn);
        return;
      }

      setErrorMessage('Socialive cannot be reached, please try again in a few minutes.');
    },
    [appLogin, keepLoggedIn, password, trackEvent, username, dispatch]
  );

  useEffect(() => {
    showHeader(false);
    const authorized = checkAuthorization('/login');
    if (authorized) {
      setMobileDevice();

      sessionStorage.removeItem('currentBroadcastHash');
      sessionStorage.removeItem('currentBroadcastId');
      sessionStorage.removeItem('currentChannelToken');
    }
    return () => showHeader(true);
  }, [showHeader, checkAuthorization, setMobileDevice]);

  return (
    <div className="login-content content-container">
      <div className="login-left">
        <div className="login-block-type">
          Meet the moment
          <br />
          with video&trade;
        </div>
      </div>
      <div className="login-right">
        <div className="login-right-content">
          <div className="login-logo-container">
            <img className="login-logo" src="/sl_logo_name_v2_light.png" alt="Socialive Logo" />
          </div>
          <h1 className="login-header">Welcome to Socialive</h1>
          <h2 className="login-subheader">The video content creation platform</h2>
          <form name="login" id="loginForm" onSubmit={handleSubmit}>
            <fieldset>
              <label className="text-input-label" htmlFor="userName">
                EMAIL
                <input type="text" id="userName" onChange={handleUsernameChange} autoComplete="username email" />
              </label>
              <label className="text-input-label" htmlFor="password">
                PASSWORD
                <input type="password" id="password" onChange={handlePasswordChange} autoComplete="current-password" />
              </label>
              <br />
              <label className="checkbox-label" htmlFor="login-keep-signed-in">
                <input
                  type="checkbox"
                  id="login-keep-signed-in"
                  onChange={handleKeepMeSignedIn}
                  checked={keepLoggedIn}
                />
                Keep me signed in
              </label>
              <Link to="/resetpassword" className="forgot-password">
                Forgot password?
              </Link>
              <div className="submit-container">
                <button
                  disabled={formValidationState === 'invalid' || loading}
                  className={`icon-arrow-circle ${formValidationState}`}
                  type="submit"
                  id="submitButton"
                  value=""
                >
                  <span className="submit-text">Sign in&nbsp;</span>
                </button>
                <Link to="/login-sso" className="sso-btn">
                  <span className="submit-text">Sign in using SSO</span>
                </Link>
              </div>
            </fieldset>
          </form>
          {errorMessage.length > 0 && (
            <div className="error-bloc">
              <span className="error">{errorMessage}</span>
            </div>
          )}
          <div className="copyright">
            <span className="byline">Copyright © 2007 -{currentYear} Socialive. All rights reserved. | </span>
            <a
              href="https://www.socialive.us/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="byline"
            >
              Privacy
            </a>
            <span className="byline"> | </span>
            <a
              href="https://www.socialive.us/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              className="byline"
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

Login.propTypes = {
  appLogin: PropTypes.func.isRequired,
  checkAuthorization: PropTypes.func.isRequired,
  currentYear: PropTypes.number.isRequired,
  setMobileDevice: PropTypes.func.isRequired,
  showHeader: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
};

export default Login;
